import Breadcrumb from "components/Common/Breadcrumb"
import TableContainer from "components/Common/TableContainer"
import { SimpleStringValue } from "helpers/common_helper_functions"
import React, { useEffect, useMemo, useState } from "react"
import { Col, Row, Card, CardBody, Container, Button } from "reactstrap"
import dayjs from "dayjs"
import { DateRangePicker } from "rsuite"
import { getPredefinedDateRanges } from "helpers/dateRanges"
import "rsuite/dist/rsuite.min.css"
import { getSubscriptionCreditNotes } from "helpers/backendHelpers/manageSubscription"
import { exportStyledXlsx } from "helpers/exportExcel"
import { CSVLink } from "react-csv"

const CreditNoteReport = () => {
  const [creditNotes, setCreditNotes] = useState([])
  const [csvData, setCsvData] = useState([])
  const [reportUrl, setReportUrl] = useState("")
  const [date, setDate] = useState({
    startDate: dayjs().startOf("month").format("YYYY-MM-DD") + "T00:00:00.000Z",
    endDate: dayjs().endOf("month").format("YYYY-MM-DD") + "T00:00:00.000Z",
  })

  const fetchCreditNotes = async (startDate = date.startDate, endDate = date.endDate) => {
    try {
      const response = await getSubscriptionCreditNotes({ startDate, endDate })
      const data = response?.payload?.data || []
      setCreditNotes(data)
      setReportUrl(response?.payload?.url || "")
      setCsvData(data.map(item => ({
        "Credit Note No": item.creditNoteNoDisplay || `CN-${item.creditNoteNo}`,
        "Credit Note Date": dayjs(item.creditNoteDate).format("DD/MM/YYYY"),
        "Invoice No": item.originalInvoiceNo || "-",
        "Invoice Date": item.originalInvoiceDate ? dayjs(item.originalInvoiceDate).format("DD/MM/YYYY") : "-",
        "Customer Name": item.customerName || "-",
        "Firm Name": item.firmName || "-",
        "GST NO.": item.GSTNo || "-",
        "Plan Name": item.planName || "-",
        "Taxable Amount": Number(item.taxableAmount || 0),
        "Tax Amount": Number(item.taxAmount || 0),
        "Total Amount": Number(item.totalAmount || 0),
        "Refund Amount": Number(item.refundAmount || 0),
        "Reason": item.reason || "-",
      })))
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchCreditNotes()
  }, [])

  const exportXlsx = () => {
    const fileName = date.startDate && date.endDate
      ? `Bill-Saathi-Credit-Notes-${dayjs(date.startDate).format("DD-MM-YYYY")}-${dayjs(date.endDate).format("DD-MM-YYYY")}`
      : "Bill-Saathi-Credit-Notes-All-Time"
    exportStyledXlsx({
      rows: csvData,
      fileName,
    })
  }

  const columns = useMemo(
    () => [
      {
        Header: "Credit Note No",
        accessor: "creditNoteNoDisplay",
        filterable: true,
        Cell: cellProps => <SimpleStringValue {...cellProps} />,
      },
      {
        Header: "Credit Note Date",
        accessor: "creditNoteDate",
        filterable: true,
        Cell: cellProps => dayjs(cellProps.row.original.creditNoteDate).format("DD/MM/YYYY"),
      },
      {
        Header: "Invoice No",
        accessor: "originalInvoiceNo",
        filterable: true,
        Cell: cellProps => <SimpleStringValue {...cellProps} />,
      },
      {
        Header: "Customer Name",
        accessor: "customerName",
        filterable: true,
        Cell: cellProps => <SimpleStringValue {...cellProps} />,
      },
      {
        Header: "Firm Name",
        accessor: "firmName",
        filterable: true,
        Cell: cellProps => <SimpleStringValue {...cellProps} />,
      },
      {
        Header: "GST NO.",
        accessor: "GSTNo",
        filterable: true,
        Cell: cellProps => <SimpleStringValue {...cellProps} />,
      },
      {
        Header: "Plan Name",
        accessor: "planName",
        filterable: true,
        Cell: cellProps => <SimpleStringValue {...cellProps} />,
      },
      {
        Header: "Taxable Amount",
        accessor: "taxableAmount",
        filterable: true,
        Cell: cellProps => <SimpleStringValue {...cellProps} />,
      },
      {
        Header: "Tax Amount",
        accessor: "taxAmount",
        filterable: true,
        Cell: cellProps => <SimpleStringValue {...cellProps} />,
      },
      {
        Header: "Total Amount",
        accessor: "totalAmount",
        filterable: true,
        Cell: cellProps => <SimpleStringValue {...cellProps} />,
      },
      {
        Header: "Credit Note",
        accessor: "pdfUrl",
        filterable: false,
        Cell: cellProps => {
          const pdfUrl = cellProps.row.original?.pdfUrl
          if (!pdfUrl) return "-"
          return (
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary" download>
              Download
            </a>
          )
        },
      },
    ],
    []
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Credit Note Report" />
        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                <div style={{ position: "absolute", left: "30%", zIndex: "1" }}>
                  <DateRangePicker
                    ranges={getPredefinedDateRanges()}
                    value={date.startDate && date.endDate ? [new Date(date.startDate), new Date(date.endDate)] : null}
                    placeholder="selected Date"
                    className="range-picker mb-2 date-picker-div"
                    onChange={e => {
                      if (e !== null) {
                        let [startDate, endDate] = e
                        startDate = dayjs(startDate).format("YYYY-MM-DD") + "T00:00:00.000Z"
                        endDate = dayjs(endDate).format("YYYY-MM-DD") + "T23:59:59.000Z"
                        setDate({ startDate, endDate })
                        fetchCreditNotes(startDate, endDate)
                      } else {
                        setDate({ startDate: "", endDate: "" })
                        fetchCreditNotes("", "")
                      }
                    }}
                    showOneCalendar
                    format="dd/MM/yyyy"
                  />
                </div>
                <div
                  className="d-flex align-items-center gap-3"
                  style={{ position: "absolute", right: "28px", zIndex: "1" }}
                >
                  <Button color="primary" className="d-inline-flex align-items-center">
                    <CSVLink
                      filename={date.startDate && date.endDate
                        ? `Bill-Saathi-Credit-Notes-${dayjs(date.startDate).format("DD-MM-YYYY")}-${dayjs(date.endDate).format("DD-MM-YYYY")}`
                        : "Bill-Saathi-Credit-Notes-All-Time"}
                      data={csvData}
                      className="csv-export-button text-white"
                    >
                      <i className="mdi mdi-export-variant me-1" />
                      Export CSV
                    </CSVLink>
                  </Button>
                  <Button color="primary" onClick={exportXlsx} className="d-inline-flex align-items-center">
                    <i className="mdi mdi-file-excel me-1" />
                    Export XLSX
                  </Button>
                  {reportUrl && (
                    <a href={reportUrl} target="_blank" rel="noopener noreferrer" className="btn btn-danger d-inline-flex align-items-center">
                      <i className="mdi mdi-file-pdf me-1" />
                      Export PDF
                    </a>
                  )}
                </div>
                <TableContainer
                  columns={columns}
                  data={creditNotes}
                  isGlobalFilter={true}
                  isAddOptions={true}
                  canExportCsv={false}
                  customPageSize={100}
                  className="custom-header-css"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default CreditNoteReport
